/**
 * Renders a small dot that follows the pointer on top of the regular
 * operating-system cursor. Elements can opt into a named mode via
 * `data-cursor="mode"` and provide a label via `data-cursor-text="View"`
 * (shown for `data-cursor="text"`). Interactive elements without an explicit
 * `data-cursor` attribute don't change the dot.
 *
 * Automatically disabled on touch devices and when the user prefers reduced motion.
 */

const INTERACTIVE_SELECTOR =
  '[data-cursor], a, button, input, textarea, select, summary, [role="button"], label[for]';

// Higher = snappier, lower = more trailing lag.
const EASE = 0.2;

class CustomCursor extends HTMLElement {
  #x = 0;
  #y = 0;
  #dotX = 0;
  #dotY = 0;
  #hasMoved = false;
  #raf = 0;
  #dot;

  connectedCallback() {
    const supportsFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!supportsFinePointer || prefersReducedMotion) {
      this.remove();
      return;
    }

    this.#dot = document.createElement('div');
    this.#dot.className = 'custom-cursor__dot';
    this.append(this.#dot);

    document.addEventListener('pointermove', this.#onPointerMove, { passive: true });
    document.addEventListener('pointerover', this.#onPointerOver, { passive: true });
    document.addEventListener('pointerout', this.#onPointerOut, { passive: true });
    document.addEventListener('pointerdown', this.#onPointerDown, { passive: true });
    document.addEventListener('pointerup', this.#onPointerUp, { passive: true });
    document.addEventListener('mouseleave', this.#onDocumentLeave);

    this.#raf = requestAnimationFrame(this.#tick);
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#raf);
    document.removeEventListener('pointermove', this.#onPointerMove);
    document.removeEventListener('pointerover', this.#onPointerOver);
    document.removeEventListener('pointerout', this.#onPointerOut);
    document.removeEventListener('pointerdown', this.#onPointerDown);
    document.removeEventListener('pointerup', this.#onPointerUp);
    document.removeEventListener('mouseleave', this.#onDocumentLeave);
  }

  #onPointerMove = (/** @type {PointerEvent} */ event) => {
    this.#x = event.clientX;
    this.#y = event.clientY;

    if (!this.#hasMoved) {
      // Snap to the initial position instead of trailing in from the corner.
      this.#dotX = this.#x;
      this.#dotY = this.#y;
      this.#hasMoved = true;
    }

    this.setAttribute('visible', '');
  };

  #onDocumentLeave = () => {
    this.removeAttribute('visible');
  };

  #onPointerDown = () => {
    this.setAttribute('pressed', '');
  };

  #onPointerUp = () => {
    this.removeAttribute('pressed');
  };

  #onPointerOver = (/** @type {PointerEvent} */ event) => {
    const target = event.target instanceof Element ? event.target.closest(INTERACTIVE_SELECTOR) : null;
    if (!target) return;

    const mode = target.getAttribute('data-cursor');
    if (mode) {
      this.dataset.mode = mode;
    } else {
      delete this.dataset.mode;
    }

    const text = target.getAttribute('data-cursor-text');
    if (this.#dot) this.#dot.textContent = text || '';
  };

  #onPointerOut = (/** @type {PointerEvent} */ event) => {
    const target = event.target instanceof Element ? event.target.closest(INTERACTIVE_SELECTOR) : null;
    if (!target) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && target.contains(relatedTarget)) return;

    delete this.dataset.mode;
    if (this.#dot) this.#dot.textContent = '';
  };

  #tick = () => {
    this.#dotX += (this.#x - this.#dotX) * EASE;
    this.#dotY += (this.#y - this.#dotY) * EASE;

    this.style.transform = `translate3d(${this.#dotX}px, ${this.#dotY}px, 0)`;

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('custom-cursor')) {
  customElements.define('custom-cursor', CustomCursor);
}
